import * as React from "react";
import {
  BooleanInput,
  minValue,
  SelectArrayInput,
  SelectInput,
  useNotify,
  useRefresh,
  SimpleForm,
  CreateBase,
  required,
  TextInput,
  NumberInput,
  DateInput,
  FormDataConsumer,
} from "react-admin";
import { Dialog, DialogTitle, DialogContent, Alert, Grid } from "@mui/material";

import { WastesChoises } from "../common/consts/wasteList.const";
import { EquipmentNameList } from "../common/consts/EquipmentNameList.const";
import { EquipmentSizeList } from "../common/consts/EquipmentSize.const";
import { EquipmentUnitList } from "../common/consts/RquipmentUnit.const";
import { DialogFormToolbar } from "../common/components/DialogFormToolbar";
import {
  FrequencyChoices,
  MonthChoices,
  OrdinalChoices,
  WeekdayChoices,
} from "../interfaces/Equipment.interface";

export const EquipmentCreateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  siteId: string;
}> = ({ open, onClose, siteId }) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const defaultValues = {
    site: siteId,
    active: true,
    density: 0,
    quantity: 1,
    collectionPlan: {
      enabled: true,
      frequency: "WEEKLY",
      weekdays: ["MONDAY"],
      ordinal: "FIRST",
      monthlyWeekday: "MONDAY",
      weekOffset: 0,
      anchorMonth: 1,
      timezone: "Europe/Paris",
      notes: "",
    },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Ajouter un équipement</DialogTitle>
      <DialogContent dividers>
        <CreateBase
          resource="equipments"
          mutationOptions={{
            onSuccess: () => {
              notify("Équipement créé", { type: "success" });
              onClose();
              refresh();
            },
            onError: (error: any) =>
              notify(error?.message ?? "Erreur lors de la création", {
                type: "error",
              }),
          }}
        >
          <SimpleForm
            toolbar={<DialogFormToolbar onCancel={onClose} />}
            defaultValues={defaultValues}
          >
            <Alert color="warning" sx={{ mb: 2 }}>
              Si vous sélectionnez le même équipement (nom + taille + unité +
              déchet) déjà existant sur ce site, la quantité sera mise à jour.
            </Alert>
            <TextInput source="site" style={{ display: "none" }} />

            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: 3 }}>
                <SelectInput
                  source="name"
                  label="Nom"
                  choices={EquipmentNameList}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <SelectInput
                  source="size"
                  label="Taille"
                  choices={EquipmentSizeList}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <SelectInput
                  source="unit"
                  label="Unité"
                  choices={EquipmentUnitList}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <NumberInput
                  source="quantity"
                  label="Quantité sur le site"
                  defaultValue={1}
                  validate={[required(), minValue(1)]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                  source="waste"
                  label="Déchet"
                  choices={WastesChoises}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <NumberInput
                  source="density"
                  label="Densité"
                  validate={[required(), minValue(0)]}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mt: 3, mb: 1 }}>
                  Configuration de la fréquence de collecte (enregistrée avec
                  l'équipement).
                </Alert>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <SelectInput
                  source="collectionPlan.frequency"
                  label="Fréquence"
                  choices={FrequencyChoices}
                  validate={[required()]}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormDataConsumer>
                  {({ formData }) => {
                    const freq = formData?.collectionPlan?.frequency;

                    const isWeekly =
                      freq === "DAILY" ||
                      freq === "WEEKLY" ||
                      freq === "BIWEEKLY";

                    const isMonthlyFamily =
                      freq === "MONTHLY" ||
                      freq === "QUARTERLY" ||
                      freq === "ANNUALLY";

                    return (
                      <>
                        {/* DAILY/WEEKLY/BIWEEKLY => jours */}
                        {isWeekly && (
                          <Grid container spacing={1}>
                            <Grid size={{ xs: 12 }}>
                              <SelectArrayInput
                                source="collectionPlan.weekdays"
                                label="Jours"
                                choices={WeekdayChoices}
                                helperText="Choisissez un ou plusieurs jours de collecte."
                                validate={[required()]}
                                fullWidth
                              />
                            </Grid>
                            {freq === "BIWEEKLY" && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <SelectInput
                                  source="collectionPlan.weekOffset"
                                  label="Décalage de quinzaine"
                                  choices={[
                                    { id: 0, name: "Semaine paire (offset 0)" },
                                    {
                                      id: 1,
                                      name: "Semaine impaire (offset 1)",
                                    },
                                  ]}
                                  fullWidth
                                />
                              </Grid>
                            )}
                          </Grid>
                        )}

                        {/* MONTHLY/QUARTERLY/ANNUALLY => ordinal + weekday (+ anchorMonth) */}
                        {isMonthlyFamily && (
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <SelectInput
                                source="collectionPlan.ordinal"
                                label="Ordre dans le mois"
                                choices={OrdinalChoices}
                                validate={[required()]}
                                fullWidth
                              />
                            </Grid>

                            {formData?.collectionPlan?.ordinal !== "ALL" && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <SelectInput
                                  source="collectionPlan.monthlyWeekday"
                                  label="Jour de la semaine"
                                  choices={WeekdayChoices}
                                  validate={[required()]}
                                  fullWidth
                                />
                              </Grid>
                            )}

                            {(freq === "QUARTERLY" || freq === "ANNUALLY") && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <SelectInput
                                  source="collectionPlan.anchorMonth"
                                  label="Mois de départ"
                                  choices={MonthChoices}
                                  fullWidth
                                />
                              </Grid>
                            )}
                          </Grid>
                        )}

                        {/* Commun */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <DateInput
                              source="collectionPlan.startDate"
                              label="Début de validité"
                              validate={[required()]}
                              fullWidth
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <DateInput
                              source="collectionPlan.endDate"
                              label="Fin de validité"
                              validate={[required()]}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextInput
                              source="collectionPlan.timezone"
                              label="Fuseau horaire"
                              defaultValue="Europe/Paris"
                              helperText="Ex: Europe/Paris"
                              validate={[required()]}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextInput
                              source="collectionPlan.notes"
                              label="Notes"
                              multiline
                              minRows={2}
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </>
                    );
                  }}
                </FormDataConsumer>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <BooleanInput label="Actif" source="active" />
              </Grid>
            </Grid>
          </SimpleForm>
        </CreateBase>
      </DialogContent>
    </Dialog>
  );
};
